import React, { useCallback, useEffect, useState } from "react";
import { Trophy, Copy, Users, CurrencyDollarSimple, Sparkle, Share, Crown, Bank, ArrowRight, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { debugLog } from "../lib/debug";

export default function Affiliate() {
  const [me, setMe] = useState(null);
  const [board, setBoard] = useState([]);
  const [payout, setPayout] = useState(null);
  const [busy, setBusy] = useState(false);
  const location = useLocation();

  const loadAll = useCallback(() => {
    api.get("/referrals/me").then(r => setMe(r.data)).catch(() => {});
    api.get("/referrals/leaderboard").then(r => setBoard(r.data)).catch(() => {});
    api.get("/payouts/status").then(r => setPayout(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Show toast on return from Razorpay/manual onboarding
  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    if (qs.get("onboard_complete")) {
      toast.success("Payout details submitted — our team will reach out shortly");
      loadAll();
    } else if (qs.get("onboard_refresh")) {
      toast.info("Onboarding interrupted. Click 'Add payout details' to resume.");
    }
  }, [location.search, loadAll]);

  const link = me ? `${window.location.origin}/?ref=${me.code}` : "";

  const copy = () => {
    navigator.clipboard.writeText(link);
    toast.success("Link copied — share it everywhere");
  };

  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: "Try ClipForge AI",
          text: "Forge viral shorts in 30 seconds — get 50 free AI credits with my link",
          url: link,
        });
      } else {
        copy();
      }
    } catch (e) {
      // User-cancelled share is normal; just log for debugging
      debugLog("share cancelled or unsupported:", e?.message);
    }
  };

  const onboard = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payouts/onboard", { return_url: window.location.origin });
      if (data.status === "redirect" && data.url) {
        window.location.href = data.url;
      } else if (data.status === "manual_review") {
        toast.info("Manual review queued", { description: data.message });
        loadAll();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Onboarding failed");
    } finally { setBusy(false); }
  };

  const requestPayout = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payouts/request", {});
      if (data.status === "paid") {
        toast.success(`Payout of $${data.amount.toFixed(2)} sent to your bank`);
      } else if (data.status === "manual_review") {
        toast.info(`Payout of $${data.amount.toFixed(2)} queued for manual review`, { description: data.message });
      }
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payout failed");
    } finally { setBusy(false); }
  };

  const pending = payout?.pending_commission ?? me?.pending_commission ?? 0;
  const onboarded = !!payout?.onboarded;
  const onboardingStarted = payout && payout.status !== "not_started";

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="affiliate-page">
      <p className="text-[#FF3B6B] text-sm font-mono">// AMBASSADOR PROGRAM</p>
      <h1 className="text-3xl md:text-4xl font-bold tracking-tighter mt-2">Refer creators, earn 30%.</h1>
      <p className="text-white/50 mt-1">Share your link. Every Pro/Premium signup pays you 30% recurring commission.</p>

      <div className="mt-8 glass-strong rounded-2xl p-6 md:p-8 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-[300px] h-[300px] bg-[#FF3B6B]/15 blur-[100px] rounded-full pointer-events-none" />
        <div className="relative">
          <p className="text-xs uppercase tracking-wider text-white/50">Your ambassador link</p>
          <div className="mt-3 flex items-stretch gap-2">
            <div className="flex-1 glass rounded-lg px-4 py-3 font-mono text-sm break-all" data-testid="affiliate-link-box">
              {link || "—"}
            </div>
            <button onClick={copy} className="btn-primary px-4 rounded-lg flex items-center gap-2" data-testid="copy-affiliate-link"><Copy size={16} /></button>
            <button onClick={share} className="bg-white text-black px-4 rounded-lg flex items-center gap-2 hover:bg-white/90" data-testid="share-affiliate-link"><Share size={16} /></button>
          </div>
          <p className="mt-3 text-xs text-white/50">Your code: <span className="font-mono text-[#FF3B6B]" data-testid="affiliate-code">{me?.code || "..."}</span></p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Total signups", value: me?.total_signups ?? 0, icon: Users, color: "#00E5FF" },
          { label: "Paid conversions", value: me?.paid_signups ?? 0, icon: Sparkle, color: "#EAB308" },
          { label: "Pending commission", value: `₹${(me?.pending_commission ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`, icon: CurrencyDollarSimple, color: "#FF3B6B" },
          { label: "Lifetime earnings", value: `₹${(me?.lifetime_commission ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`, icon: Trophy, color: "#FF3B6B", accent: true },
        ].map((s, i) => (
          <div key={s.label} className="glass rounded-xl p-5" data-testid={`affiliate-stat-${i}`}>
            <s.icon size={20} style={{ color: s.color }} weight="duotone" />
            <p className="mt-3 text-xs text-white/50 uppercase tracking-wider">{s.label}</p>
            <p className={`mt-1 font-mono text-2xl ${s.accent ? "text-[#FF3B6B]" : ""}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Milestone reward progress */}
      {me?.milestone && (
        <div className="mt-6 glass-strong rounded-2xl p-6" data-testid="milestone-card">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Trophy size={24} weight="duotone" className="text-[#EAB308]" />
              <div>
                <p className="text-sm font-semibold tracking-tight">
                  {me.milestone.unlocked ? "Milestone unlocked!" : "Next milestone"}
                </p>
                <p className="text-xs text-white/60 mt-0.5">
                  {me.milestone.unlocked
                    ? `You've earned: ${me.milestone.reward}`
                    : `${me.milestone.remaining} more paid referral${me.milestone.remaining === 1 ? "" : "s"} unlocks ${me.milestone.reward}`}
                </p>
              </div>
            </div>
            <p className="font-mono text-sm text-white/80" data-testid="milestone-progress-text">
              {me.milestone.progress} / {me.milestone.threshold}
            </p>
          </div>
          <div className="mt-4 h-2 w-full rounded-full bg-white/5 overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-[#FF3B6B] to-[#EAB308] transition-all duration-700"
              style={{ width: `${Math.min(100, (me.milestone.progress / me.milestone.threshold) * 100)}%` }}
              data-testid="milestone-progress-bar"
            />
          </div>
        </div>
      )}

      {/* Payouts panel */}
      <div className="mt-8 glass-strong rounded-2xl p-6 md:p-8" data-testid="payouts-panel">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2">
              <Bank size={22} className="text-[#00E5FF]" weight="duotone" />
              <h2 className="text-xl font-bold tracking-tighter">Payouts</h2>
              {onboarded && (
                <span className="text-xs flex items-center gap-1 text-[#00E5FF] font-mono px-2 py-0.5 rounded-full bg-[#00E5FF]/10">
                  <CheckCircle size={12} weight="fill" /> Connected
                </span>
              )}
            </div>
            <p className="text-sm text-white/50 mt-1">
              {onboarded
                ? "Payout details saved. Request a payout to receive pending commission via UPI / bank transfer (₹)."
                : "Add your UPI ID or bank details once — our team will process payouts manually within 3 business days. (RazorpayX automated payouts coming soon.)"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {!onboarded ? (
              <button
                onClick={onboard}
                disabled={busy}
                className="btn-primary px-5 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                data-testid="payout-onboard-btn"
              >
                {onboardingStarted ? "Resume setup" : "Add payout details"} <ArrowRight size={14} />
              </button>
            ) : (
              <button
                onClick={requestPayout}
                disabled={busy || pending <= 0}
                className="btn-primary px-5 py-2.5 rounded-lg text-sm font-medium disabled:opacity-40"
                data-testid="request-payout-btn"
                title={pending <= 0 ? "No pending commission" : ""}
              >
                Request payout (₹{pending.toFixed(2)})
              </button>
            )}
          </div>
        </div>
        {payout?.status === "manual_review" && (
          <div className="mt-4 text-xs text-[#EAB308] bg-[#EAB308]/10 rounded-lg px-3 py-2 font-mono" data-testid="manual-review-notice">
            ⚠ Pending balance ₹{pending.toFixed(2)} queued for manual review. Our team processes payouts within 3 business days.
          </div>
        )}
      </div>

      {/* Leaderboard */}
      <h2 className="mt-12 text-lg font-semibold flex items-center gap-2">
        <Crown className="text-[#EAB308]" weight="fill" /> Top ambassadors this month
      </h2>
      <div className="mt-4 glass rounded-xl overflow-hidden">
        {board.length === 0 && (
          <div className="p-8 text-center text-white/40">
            <Trophy size={40} className="mx-auto mb-2 text-white/20" />
            <p>Leaderboard is still warming up. Be the first to convert a paid referral.</p>
          </div>
        )}
        {board.map((b, i) => {
          const rankBg = (
            i === 0 ? "bg-[#EAB308] text-black"
              : i === 1 ? "bg-white/30"
                : i === 2 ? "bg-[#FF3B6B]/40"
                  : "bg-white/10"
          );
          return (
            <div key={`${b.name}-${i}`} className="flex items-center gap-4 p-4 border-b border-white/5 last:border-none" data-testid={`leaderboard-${i}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${rankBg}`}>
                {i + 1}
              </div>
              {b.picture
                ? <img src={b.picture} alt="" className="w-9 h-9 rounded-full" />
                : <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#FF3B6B] to-[#EAB308]" />}
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{b.name}</p>
                <p className="text-xs text-white/50">{b.paid_signups} paid signups</p>
              </div>
              <div className="font-mono text-sm text-[#FF3B6B] font-bold">₹{b.total_commission.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</div>
            </div>
          );
        })}
      </div>

      {/* How it works */}
      <div className="mt-12 glass rounded-2xl p-8">
        <h3 className="text-xl font-bold tracking-tighter">How it works</h3>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { n: "01", t: "Share your link", d: "Drop it in your bio, in tutorials, anywhere." },
            { n: "02", t: "They sign up", d: "Your referrals get tracked automatically. We notify you on each conversion." },
            { n: "03", t: "Earn 30%", d: "Recurring commission on every Pro/Premium subscription, paid via UPI/bank transfer (₹)." },
          ].map(s => (
            <div key={s.n}>
              <div className="font-mono text-3xl text-[#FF3B6B]/30">{s.n}</div>
              <p className="mt-2 font-semibold">{s.t}</p>
              <p className="text-sm text-white/60 mt-1">{s.d}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
